import { FC, useMemo, useRef } from 'react'
import { ItemRendererProps } from 'react-calendar-timeline/dist/lib/items/Item'

import { BuildCircle, FiberNew } from '@mui/icons-material'

import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import { htmlDecode } from '../../../../../../helpers/dom'
import { shadeColor } from '../../../../../../helpers/shadeColor'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { LiveEventTagGroup } from '../../../../types/LiveEventTagGroup'
import { TrackingEventTimelineItem } from '../../../../types/LiveEvents'
import { LiveEventInfoTooltipContent } from './LiveEventInfoTooltipContent'

const defaultTimelineItemBGColor = '#273143'
const highlightedTimelineItemBGColor = '#38a73e'
const inactiveEventColor = '#b13d37'
const maxDescriptionLength = 300

export const LiveEventsCalendarTrackingEventItem: FC<ItemRendererProps<TrackingEventTimelineItem>> = ({ item, itemContext, getItemProps, getResizeProps }) => {
  const userLanguage = useCurrentUserLanguage()
  const itemRef = useRef(null)
  const { left: leftResizeProps, right: rightResizeProps } = getResizeProps()
  const highlightItem = item.highlighted
  const newItem = item.new
  const firstMechanicChange = item.firstMechanicChange
  const inactive = item.trackingEvent.active === false

  const descriptionRawHtml = useMemo(() => {
    const descriptionHTML = item.trackingEvent.comment?.content?.comment?.[userLanguage]
      ? item.trackingEvent.comment?.content?.comment?.[userLanguage]
      : item.trackingEvent.comment?.content?.comment?.['en']

    if (descriptionHTML) {
      const paragraphs = descriptionHTML.split('</p>')

      // find the first paragraph with content
      const paragraph = paragraphs.find((paragraph) => {
        return !!paragraph.replace(/<\/?[^>]+(>|$)/g, '')
      })

      const strippedParagraph = paragraph?.replace(/<\/?[^>]+(>|$)/g, '') || ''
      const decodedParagraph = htmlDecode(strippedParagraph)

      if (decodedParagraph && decodedParagraph.length > maxDescriptionLength) {
        return decodedParagraph.substring(0, maxDescriptionLength) + '...'
      } else {
        return decodedParagraph
      }
    }
  }, [item, userLanguage])

  let bgColor = useMemo(() => {
    if (highlightItem && item.tagGroup && item.tagGroup.colorHex) {
      return highlightedTimelineItemBGColor
    } else if (newItem && item.tagGroup && item.tagGroup.colorHex) {
      return shadeColor(item.tagGroup.colorHex, -50)
    } else if (firstMechanicChange && item.tagGroup && item.tagGroup.colorHex) {
      return shadeColor(item.tagGroup.colorHex, -25)
    } else if (item.tagGroup && item.tagGroup.colorHex) {
      return item.tagGroup.colorHex
    } else {
      return defaultTimelineItemBGColor
    }
  }, [newItem, firstMechanicChange, highlightItem, item.tagGroup])

  return (
    <GRTooltip
      content={
        <LiveEventInfoTooltipContent
          item={item}
          itemContext={itemContext}
          descriptionRawHtml={descriptionRawHtml}
          bgColor={bgColor}
          newItem={newItem}
          mechanicChange={firstMechanicChange}
          firstInstance={false}
          inactive={inactive}
        />
      }
      popoverBorderColor={bgColor}
      anchorRef={itemRef}
      disableMaxWidth={true}
    >
      <div
        {...getItemProps({
          style: {
            background: bgColor,
            color: 'white',
            borderTopWidth: 1,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderLeftWidth: 1,
            borderStyle: 'solid',
            borderColor: 'white',
            borderTop: inactive ? `3px solid ${inactiveEventColor}` : '1px solid white',
            borderRadius: 10,
            fontSize: item.tagGroup && item.tagGroup.id === LiveEventTagGroup.DEFINING_OCCURRENCES ? '15px' : '12px',
            fontWeight: item.tagGroup && item.tagGroup.id === LiveEventTagGroup.DEFINING_OCCURRENCES ? 'bold' : 'normal',
          },
        })}
        className={item.tagGroup && item.tagGroup.id === LiveEventTagGroup.DEFINING_OCCURRENCES ? 'rct-item rct-item--SpecialEvent' : 'rct-item'}
        ref={itemRef}
      >
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

        <div
          className="rct-item-content"
          style={{
            height: itemContext.dimensions.height,
            overflow: 'hidden',
            padding: '0px 6px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            verticalAlign: 'top',
          }}
        >
          {newItem && <FiberNew sx={{ position: 'relative', fontSize: '16px', verticalAlign: 'middle', top: '-2px', mr: 1 }} />}
          {firstMechanicChange && <BuildCircle sx={{ position: 'relative', fontSize: '16px', verticalAlign: 'middle', top: '-2px', left: '-4px' }} />}
          {itemContext.title}
        </div>

        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    </GRTooltip>
  )
}
